/**
 * The page in place of the console when the account has no programme to
 * open — 0332. The words and choices come from closed-programme.ts; this only
 * draws them, on the same single sheet as sign-in.
 */

import { useEffect, useRef, useState } from 'react';

import { Callout, Loading, Panel, kitText } from '@affdice/portal-kit';

import type { AffiliateApi } from './client.ts';
import { closedProgrammeView, type ClosedProgramme, type ProgrammesLoad } from './closed-programme.ts';
import { t } from './messages.ts';

import './closed-programme.css';

export function ClosedProgrammePage({
  api,
  closed,
  onChosen,
  onSignedOut,
}: {
  api: AffiliateApi;
  closed: ClosedProgramme;
  /** A live programme is now selected: open the console on it. */
  onChosen: () => void;
  onSignedOut: () => void;
}) {
  const [programmes, setProgrammes] = useState<ProgrammesLoad>({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [opening, setOpening] = useState<string | null>(null);
  const [openFailed, setOpenFailed] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let live = true;
    setProgrammes({ state: 'loading' });
    api.operators().then(
      (r) => { if (live) setProgrammes({ state: 'ready', items: r.items }); },
      () => { if (live) setProgrammes({ state: 'error' }); },
    );
    return () => { live = false; };
    // Asked again only on a retry: the host builds a new client each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const view = closedProgrammeView(closed, programmes);

  // The console vanished under the reader; move focus to what replaced it so
  // a screen reader announces the page rather than silence.
  useEffect(() => { heading.current?.focus(); }, [view.kind]);

  const open = (operatorId: string) => {
    setOpening(operatorId);
    setOpenFailed(false);
    api.selectOperator(operatorId).then(onChosen, () => {
      setOpening(null);
      setOpenFailed(true);
    });
  };

  const signOut = (
    <button
      type="button"
      disabled={signingOut}
      aria-busy={signingOut}
      onClick={() => {
        setSigningOut(true);
        // Cleared here whatever the server said, as the console's own Sign
        // out does: a browser told it signed out must stop holding the token.
        void api.logout().catch(() => {}).finally(onSignedOut);
      }}
    >
      {signingOut ? kitText('signout.busy') : kitText('signout.action')}
    </button>
  );

  return (
    <div className="main signin closed-programme">
      <div className="page-head">
        <h1 ref={heading} tabIndex={-1}>{view.title}</h1>
      </div>

      {view.kind === 'loading' ? (
        <Panel title={t('closed.choose.panel')}>
          <Loading rows={2} />
        </Panel>
      ) : null}

      {view.kind === 'choose' ? (
        <>
          <p className="closed-lead">{view.lead} {view.records}</p>
          <Panel title={t('closed.choose.panel')}>
            <div className="stack">
              {openFailed ? <Callout tone="error">{t('closed.choose.failed')}</Callout> : null}
              <ul className="closed-choices">
                {view.choices.map((c) => (
                  <li key={c.operatorId}>
                    <button
                      type="button"
                      className="closed-choice"
                      aria-label={c.hint ? `${c.action} (${c.hint})` : c.action}
                      disabled={opening !== null}
                      aria-busy={opening === c.operatorId}
                      onClick={() => open(c.operatorId)}
                    >
                      <span className="closed-choice-name">{c.label}</span>
                      {c.hint ? <span className="closed-choice-hint">{c.hint}</span> : null}
                      <span className="closed-choice-go" aria-hidden="true">
                        {opening === c.operatorId ? t('closed.choose.opening') : '→'}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </Panel>
        </>
      ) : null}

      {view.kind === 'none' ? (
        <>
          {view.failed ? (
            <Callout tone="caution">
              <span className="closed-retry">
                <span>{t('closed.lookup.failed')}</span>
                <button type="button" onClick={() => setAttempt((n) => n + 1)}>
                  {t('closed.lookup.retry')}
                </button>
              </span>
            </Callout>
          ) : null}
          <p className="closed-lead">{view.lead ? `${view.lead} ${view.records}` : view.records}</p>
          <Panel title={t('closed.none.panel')}>
            <ul className="closed-steps">
              {view.steps.map((step) => <li key={step}>{step}</li>)}
            </ul>
          </Panel>
        </>
      ) : null}

      <div className="closed-actions">{signOut}</div>
    </div>
  );
}
